import { UseQueryOptions, UseMutationOptions, useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ApiCalls } from '../api/api';
import { InitialData, TimeUserFeatures } from '../api/generated/data-contracts';
import { useSetQueryData } from './useSetQueryData';
import { configQKey, useConfigValues } from './queries/useConfigValues';
import { FunctionOr } from '../types/FunctionOr';
import { unwrap } from '../helpers/unwrap';

const useSetConfigValuesCache = () => {
    return useSetQueryData<InitialData>(configQKey);
};

export const useMutateConfigValue = (options?: UseMutationOptions<void, unknown, TimeUserFeatures, unknown>) => {
    return useMutation({
        mutationFn: async (dto: TimeUserFeatures) => {
            return ApiCalls.configurationBasicCreate(dto).then(() => {});
        },
        ...(options || {}),
    });
};

export const useSetConfigValue = (options?: UseMutationOptions<void, unknown, TimeUserFeatures, unknown>) => {
    const { mutateAsync } = useMutateConfigValue(options);
    const setConfigValueCache = useSetConfigValuesCache();
    return useCallback(
        (newConfig: FunctionOr<TimeUserFeatures>) => {
            return new Promise<void>((res, rej) => {
                setConfigValueCache(configs => {
                    const nextConfig = unwrap(newConfig, configs.configuration);
                    mutateAsync(nextConfig).then(res).catch(rej);
                    return {
                        ...configs,
                        configuration: nextConfig,
                    };
                });
            });
        },
        [mutateAsync, setConfigValueCache],
    );
};

export const useConfigValuesState = (
    options?: Omit<UseQueryOptions<InitialData, unknown, InitialData, string[]>, 'initialData' | 'queryKey' | 'queryFn'> & {
        initialData?: () => undefined;
    },
) => {
    const { mutateAsync, isLoading } = useMutateConfigValue();
    const setConfigValueCache = useSetConfigValuesCache();
    const setConfigValue = useCallback(
        (newConfig: FunctionOr<TimeUserFeatures>) => {
            return new Promise<void>((res, rej) => {
                setConfigValueCache(configs => {
                    const nextConfig = unwrap(newConfig, configs.configuration);
                    mutateAsync(nextConfig).then(res).catch(rej);
                    return {
                        ...configs,
                        configuration: nextConfig,
                    };
                });
            });
        },
        [mutateAsync, setConfigValueCache],
    );
    const query = useConfigValues({ suspense: true, ...options });
    return {
        ...query,
        initialData: query.data,
        setConfigValue,
        setConfigValueCache,
        isLoading: query.isLoading || isLoading,
    };
};
